import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';

import useLanguage from '@/components/common/hooks/useLanguage';
import BottomSheet from '@/components/payout/BottomSheet';
import Requisites from '@/components/payout/Requisites';
import { getRequisites } from '@/store/actions/payout';
import { PayoutGridRow } from '@/store/reducers/payout';
import { selectRequisites } from '@/store/selectors/payout';

interface Props {
  row: PayoutGridRow;
  onRename: (name: string) => void;
  onArchive: (archived: boolean) => void;
  onClose: () => void;
}

/**
 * The contractor counterpart to PayeeSheet. There is no salary and no expenses
 * to review -- their figures are typed straight into the grid -- so this is the
 * name, where their money goes, and whether they are still being paid.
 *
 * Archiving never deletes: their recorded months stay on the table, and the
 * grid's Archived switch is what hides them.
 */
const ContractorSheet: React.FC<Props> = ({
  row, onRename, onArchive, onClose,
}) => {
  const dispatch = useDispatch();
  const requisites = useSelector(selectRequisites);
  const [{ payout }] = useLanguage();
  const [name, setName] = useState(row.name);

  const payee = { contractorId: row.contractorId! };

  useEffect(() => {
    dispatch(getRequisites(payee));
  }, [row.ref]);

  const isRenamed = name.trim() && name.trim() !== row.name;
  const isReady = Boolean(requisites && requisites.payee === row.ref);

  return (
    <BottomSheet isReady={isReady} onClose={onClose}>
      <Heading>{row.name}</Heading>
        {/* Deliberately no totals: row.cells only covers the columns currently
            on screen, so a contractor with twenty months of history reads as
            "0 months, $0" in the default two-column view. */}
        <Note>{row.isArchived ? payout.archived : payout.contractor}</Note>

        <Row>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
          <Primary disabled={!isRenamed} onClick={() => onRename(name.trim())}>
            {payout.save}
          </Primary>
        </Row>

      {isReady && <Requisites payee={payee} requisite={requisites!.requisite} />}

      {row.isArchived ? (
        <Restore onClick={() => onArchive(false)}>{payout.restore}</Restore>
      ) : (
        <Archive onClick={() => onArchive(true)}>{payout.archive}</Archive>
      )}
    </BottomSheet>
  );
};




const Heading = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: #21272e;
`;

const Note = styled.div`
  font-size: 12px;
  color: #909599;
  margin: 4px 0 16px;
`;

const Row = styled.div`
  display: flex;
`;

const Input = styled.input`
  flex: 1;
  height: 44px;
  border-radius: 6px;
  border: solid 1px #dae1e8;
  padding: 0 12px;
  font-size: 14px;
  outline: none;

  :focus {
    border-color: #fb7e14;
  }
`;

const Primary = styled.button`
  height: 44px;
  margin-left: 8px;
  padding: 0 18px;
  border: none;
  border-radius: 6px;
  background: #fb7e14;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  :disabled {
    background: #dae1e8;
    cursor: default;
  }
`;

const Archive = styled.div`
  margin-top: 20px;
  font-size: 14px;
  font-weight: 600;
  color: #feaa22;
  cursor: pointer;
`;

const Restore = styled(Archive)`
  color: #188038;
`;

export default ContractorSheet;
